import { Editor } from '@tiptap/react';
import { DOMSerializer } from '@tiptap/pm/model';
import { sanitizeHtml } from '../../../utils/sanitize';

// These are plain functions, not components, so they cannot reach the
// language hook. The caller (DocumentEditor, which has `t`) hands the
// translations down; the keyboard shortcuts inside the strings are the same
// in every language.
export interface ToolbarMessages {
    clipboardBlockedCut: string;
    clipboardBlockedCopy: string;
    clipboardBlockedPaste: string;
    linkUrlPrompt: string;
}

const escapeHtml = (text: string) =>
    text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// --- Clipboard (Edit menu). Browsers only allow programmatic clipboard
// access over https/localhost and may prompt for permission; on failure we
// fall back to telling the user the keyboard shortcut.

const copySelection = async (editor: Editor, cut: boolean, messages: ToolbarMessages) => {
    const { state } = editor;
    const { from, to, empty } = state.selection;
    if (empty) return;
    const text = state.doc.textBetween(from, to, '\n');
    try {
        if (navigator.clipboard.write && window.ClipboardItem) {
            const container = document.createElement('div');
            container.appendChild(
                DOMSerializer.fromSchema(state.schema).serializeFragment(state.selection.content().content)
            );
            await navigator.clipboard.write([
                new ClipboardItem({
                    'text/html': new Blob([container.innerHTML], { type: 'text/html' }),
                    'text/plain': new Blob([text], { type: 'text/plain' }),
                }),
            ]);
        } else {
            await navigator.clipboard.writeText(text);
        }
        if (cut) {
            editor.chain().focus().deleteSelection().run();
        }
    } catch {
        alert(cut ? messages.clipboardBlockedCut : messages.clipboardBlockedCopy);
    }
};

const pasteFromClipboard = async (editor: Editor, keepFormatting: boolean, messages: ToolbarMessages) => {
    try {
        if (keepFormatting && navigator.clipboard.read) {
            const items = await navigator.clipboard.read();
            for (const item of items) {
                if (item.types.includes('text/html')) {
                    const blob = await item.getType('text/html');
                    const html = sanitizeHtml(await blob.text());
                    editor.chain().focus().insertContent(html).run();
                    return;
                }
            }
        }
        const text = await navigator.clipboard.readText();
        if (!text) return;
        const lines = text.split(/\r?\n/);
        const content = lines.length > 1
            ? lines.map((line) => `<p>${escapeHtml(line)}</p>`).join('')
            : escapeHtml(text);
        editor.chain().focus().insertContent(content).run();
    } catch {
        alert(messages.clipboardBlockedPaste);
    }
};

// --- Insert menu

export const insertLink = (editor: Editor | null, messages: ToolbarMessages) => {
    if (!editor) return;
    const existing = editor.getAttributes('link').href as string | undefined;
    const input = window.prompt(messages.linkUrlPrompt, existing || '');
    if (input === null) return;
    const trimmed = input.trim();
    if (!trimmed) {
        editor.chain().focus().extendMarkRange('link').unsetLink().run();
        return;
    }
    const href = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    if (editor.state.selection.empty && !existing) {
        // Nothing selected: insert the URL itself as a link
        editor.chain().focus().insertContent({
            type: 'text',
            text: href,
            marks: [{ type: 'link', attrs: { href } }],
        }).run();
    } else {
        editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
    }
};

// --- Format menu

export const transformCase = (editor: Editor | null, mode: 'upper' | 'lower' | 'title') => {
    if (!editor) return;
    const { state } = editor;
    const { from, to, empty } = state.selection;
    if (empty) return;

    const toTitleCase = (text: string) =>
        text.replace(/\S+/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());

    // Collect replacements first, then apply back-to-front so earlier
    // replacements can't shift later positions.
    const replacements: { start: number; end: number; text: string }[] = [];
    state.doc.nodesBetween(from, to, (node, pos) => {
        if (!node.isText || !node.text) return;
        const start = Math.max(from, pos);
        const end = Math.min(to, pos + node.text.length);
        if (start >= end) return;
        const slice = node.text.slice(start - pos, end - pos);
        const transformed =
            mode === 'upper' ? slice.toUpperCase()
            : mode === 'lower' ? slice.toLowerCase()
            : toTitleCase(slice);
        if (transformed !== slice) {
            replacements.push({ start, end, text: transformed });
        }
    });
    if (!replacements.length) return;

    const tr = state.tr;
    for (let i = replacements.length - 1; i >= 0; i--) {
        const { start, end, text } = replacements[i];
        tr.insertText(text, start, end);
    }
    editor.view.dispatch(tr);
    editor.commands.focus();
};

export const indentSelection = (editor: Editor | null) => {
    if (!editor) return;
    if (editor.isActive('listItem')) {
        editor.chain().focus().sinkListItem('listItem').run();
    } else {
        editor.chain().focus().indent().run();
    }
};

export const outdentSelection = (editor: Editor | null) => {
    if (!editor) return;
    if (editor.isActive('listItem')) {
        editor.chain().focus().liftListItem('listItem').run();
    } else {
        editor.chain().focus().outdent().run();
    }
};

export const applyTextOutline = (editor: Editor | null, width: number, color: string) => {
    if (!editor) return;
    editor.chain().focus().setTextOutline(width, color).run();
};

export const removeTextOutline = (editor: Editor | null) => {
    if (!editor) return;
    editor.chain().focus().unsetTextOutline().run();
};

// A heading gets its size from CSS, so an inline `font-size` left on the text
// (typed at 14px, or carried in from a paste) wins over it and the heading
// looks unchanged. Applying a text style clears that inline size across the
// whole block, the way Docs/Word do.
const clearInlineFontSize = (editor: Editor) => {
    const { state } = editor;
    const textStyle = state.schema.marks.textStyle;
    if (!textStyle) return;

    // Widen the selection to the blocks it touches — the style applies to the
    // whole paragraph/heading, not just the highlighted characters.
    const { from, to } = state.selection;
    let start = from;
    let end = to;
    state.doc.nodesBetween(from, to, (node, pos) => {
        if (!node.isTextblock) return;
        start = Math.min(start, pos + 1);
        end = Math.max(end, pos + 1 + node.content.size);
    });

    const tr = state.tr;
    let changed = false;
    state.doc.nodesBetween(start, end, (node, pos) => {
        if (!node.isText) return;
        const mark = node.marks.find((m) => m.type === textStyle);
        if (!mark || mark.attrs.fontSize == null) return;
        const markFrom = Math.max(start, pos);
        const markTo = Math.min(end, pos + node.nodeSize);
        if (markFrom >= markTo) return;
        changed = true;
        const attrs = { ...mark.attrs, fontSize: null };
        // Keep the mark only if it still carries something (color, family…)
        if (Object.values(attrs).some((value) => value != null)) {
            tr.addMark(markFrom, markTo, textStyle.create(attrs));
        } else {
            tr.removeMark(markFrom, markTo, textStyle);
        }
    });
    if (changed) editor.view.dispatch(tr);
};

export const setTextStyle = (editor: Editor | null, level: number | null) => {
    if (!editor) return;
    const chain = editor.chain().focus();
    if (level === null) {
        chain.setParagraph();
    } else {
        chain.setHeading({ level: level as 1 | 2 | 3 | 4 | 5 | 6 });
    }
    // unsetFontSize covers the collapsed-cursor case (stored marks), the scan
    // below covers text that already carries a size.
    chain.unsetFontSize().run();
    clearInlineFontSize(editor);
};

export const setFontFamily = (editor: Editor | null, font: string) => {
    if (!editor) return;
    editor.chain().focus().setFontFamily(font).run();
};

export const handleFontSizeChange = (
    editor: Editor | null,
    size: number,
    setFontSize: (size: number) => void
) => {
    if (!editor || size < 6 || size > 72) return;
    setFontSize(size);
    editor.chain().focus().setFontSize(size.toString()).run();
};

export const incrementFontSize = (
    fontSize: number,
    editor: Editor | null,
    setFontSize: (size: number) => void
) => {
    const newSize = Math.min(fontSize + 1, 72);
    handleFontSizeChange(editor, newSize, setFontSize);
};

export const decrementFontSize = (
    fontSize: number,
    editor: Editor | null,
    setFontSize: (size: number) => void
) => {
    const newSize = Math.max(fontSize - 1, 6);
    handleFontSizeChange(editor, newSize, setFontSize);
};

// Black or white, whichever reads better on the given background color
// (YIQ perceived-luminance). Theme-independent: yellow highlight gets black
// text and navy gets white regardless of light/dark mode.
export const contrastTextFor = (background: string): string => {
    const hex = background.replace('#', '');
    const full = hex.length === 3 ? hex.split('').map((c) => c + c).join('') : hex;
    const r = parseInt(full.slice(0, 2), 16);
    const g = parseInt(full.slice(2, 4), 16);
    const b = parseInt(full.slice(4, 6), 16);
    if ([r, g, b].some(Number.isNaN)) return '#ffffff';
    const luminance = (r * 299 + g * 587 + b * 114) / 1000;
    return luminance > 140 ? '#000000' : '#ffffff';
};

export const setTextColor = (
    editor: Editor | null,
    color: string,
    setCurrentTextColor: (color: string) => void,
    setShowColorPicker: (picker: 'text' | 'highlight' | 'outline' | null) => void
) => {
    if (!editor) return;
    if (color === 'default') {
        // Back to the theme's text color
        editor.chain().focus().unsetColor()
            .setMark('textStyle', { userColor: null }).run();
    } else {
        // data-user-color marks this as a deliberate choice so the CSS that
        // neutralizes pasted colors leaves it alone
        editor.chain().focus().setColor(color)
            .setMark('textStyle', { userColor: 'true' }).run();
        setCurrentTextColor(color);
    }
    setShowColorPicker(null);
};

export const setHighlightColor = (
    editor: Editor | null,
    color: string,
    setCurrentHighlightColor: (color: string) => void,
    setShowColorPicker: (picker: 'text' | 'highlight' | 'outline' | null) => void,
    setCurrentTextColor?: (color: string) => void
) => {
    if (!editor) return;
    if (color === 'transparent') {
        // Removing the highlight also drops the auto-contrast text color
        editor.chain().focus().unsetHighlight().unsetColor()
            .setMark('textStyle', { userColor: null }).run();
    } else {
        // Text color follows the highlight for readability: light highlights
        // (yellow) get black text, dark ones (blue) get white — in both themes.
        const textColor = contrastTextFor(color);
        editor.chain().focus().setHighlight({ color })
            .setColor(textColor)
            .setMark('textStyle', { userColor: 'true' }).run();
        setCurrentHighlightColor(color);
        setCurrentTextColor?.(textColor);
    }
    setShowColorPicker(null);
};

export const setAlignment = (
    editor: Editor | null,
    align: 'left' | 'center' | 'right' | 'justify'
) => {
    if (!editor) return;
    editor.chain().focus().setTextAlign(align).run();
};

export const clearFormatting = (editor: Editor | null) => {
    if (!editor) return;
    editor.chain().focus().clearNodes().unsetAllMarks().run();
};

// Spacing applies to the blocks the selection touches (Docs/Word behavior),
// not to the whole document — the stylesheet still supplies the default for
// everything the user hasn't touched.

export const handleSpacingChange = (
    editor: Editor | null,
    spacing: number,
    setShowMenuDropdown: (menu: string | null) => void
) => {
    editor?.chain().focus().setLineSpacing(spacing).run();
    setShowMenuDropdown(null);
};

export const handleAddSpaceBefore = (
    editor: Editor | null,
    setShowMenuDropdown: (menu: string | null) => void
) => {
    editor?.chain().focus().addSpaceBefore().run();
    setShowMenuDropdown(null);
};

export const handleRemoveSpaceBefore = (
    editor: Editor | null,
    setShowMenuDropdown: (menu: string | null) => void
) => {
    editor?.chain().focus().removeSpaceBefore().run();
    setShowMenuDropdown(null);
};

export const applyCustomSpacing = (
    editor: Editor | null,
    lineSpacing: number,
    spacingBefore: number,
    spacingAfter: number
) => {
    editor?.chain().focus()
        .setLineSpacing(lineSpacing)
        .setBlockSpacing({ before: spacingBefore, after: spacingAfter })
        .run();
};

// Marks the block so the paginator starts a page at it. This used to insert a
// horizontal rule — a visible line across the text that moved nothing to the
// next page, which is not what the menu item says it does.
export const handleAddPageBreak = (
    editor: Editor | null,
    setShowMenuDropdown: (menu: string | null) => void
) => {
    if (!editor) return;
    editor.chain().focus().togglePageBreakBefore().run();
    setShowMenuDropdown(null);
};

export const handleMenuAction = (
    action: string,
    editor: Editor | null,
    setShowMenuDropdown: (menu: string | null) => void,
    setShowWordCountModal: (show: boolean) => void,
    setShowProofreadModal: (show: boolean) => void,
    setShowImportLocalFilesModal: ((show: boolean) => void) | undefined,
    setShowImportGoogleDocsModal: ((show: boolean) => void) | undefined,
    messages: ToolbarMessages
) => {
    if (!editor) return;

    // Screenplay element actions arrive as 'script:<type>' ('script:none'
    // clears the element back to normal text)
    if (action.startsWith('script:')) {
        const type = action.slice('script:'.length);
        editor.chain().focus().setScriptType(type === 'none' ? null : (type as any)).run();
        setShowMenuDropdown(null);
        return;
    }

    switch (action) {
        case 'print':
            window.print();
            break;
        case 'undo':
            editor.chain().focus().undo().run();
            break;
        case 'redo':
            editor.chain().focus().redo().run();
            break;
        case 'cut':
            copySelection(editor, true, messages);
            break;
        case 'copy':
            copySelection(editor, false, messages);
            break;
        case 'paste':
            pasteFromClipboard(editor, true, messages);
            break;
        case 'pastePlain':
            pasteFromClipboard(editor, false, messages);
            break;
        case 'selectAll':
            editor.chain().focus().selectAll().run();
            break;
        case 'bold':
            editor.chain().focus().toggleBold().run();
            break;
        case 'italic':
            editor.chain().focus().toggleItalic().run();
            break;
        case 'underline':
            editor.chain().focus().toggleUnderline().run();
            break;
        case 'strikethrough':
            editor.chain().focus().toggleStrike().run();
            break;
        case 'superscript':
            editor.chain().focus().unsetSubscript().toggleSuperscript().run();
            break;
        case 'subscript':
            editor.chain().focus().unsetSuperscript().toggleSubscript().run();
            break;
        case 'upperCase':
            transformCase(editor, 'upper');
            break;
        case 'lowerCase':
            transformCase(editor, 'lower');
            break;
        case 'titleCase':
            transformCase(editor, 'title');
            break;
        case 'alignLeft':
        case 'alignCenter':
        case 'alignRight':
        case 'alignJustify': {
            const align = action.replace('align', '').toLowerCase() as 'left' | 'center' | 'right' | 'justify';
            editor.chain().focus().setTextAlign(align).run();
            break;
        }
        case 'bulletList':
            editor.chain().focus().toggleBulletList().run();
            break;
        case 'orderedList':
            editor.chain().focus().toggleOrderedList().run();
            break;
        case 'taskList':
            editor.chain().focus().toggleTaskList().run();
            break;
        case 'indent':
            indentSelection(editor);
            break;
        case 'outdent':
            outdentSelection(editor);
            break;
        case 'insertLink':
            insertLink(editor, messages);
            break;
        case 'insertTable':
            editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
            break;
        case 'insertHorizontalRule':
            editor.chain().focus().setHorizontalRule().run();
            break;
        case 'tableAddRowAbove':
            editor.chain().focus().addRowBefore().run();
            break;
        case 'tableAddRowBelow':
            editor.chain().focus().addRowAfter().run();
            break;
        case 'tableAddColumnLeft':
            editor.chain().focus().addColumnBefore().run();
            break;
        case 'tableAddColumnRight':
            editor.chain().focus().addColumnAfter().run();
            break;
        case 'tableDeleteRow':
            editor.chain().focus().deleteRow().run();
            break;
        case 'tableDeleteColumn':
            editor.chain().focus().deleteColumn().run();
            break;
        case 'tableDelete':
            editor.chain().focus().deleteTable().run();
            break;
        case 'tableToggleHeaderRow':
            editor.chain().focus().toggleHeaderRow().run();
            break;
        case 'wordCount':
            setShowWordCountModal(true);
            break;
        case 'proofreadSettings':
            setShowProofreadModal(true);
            break;
        case 'importLocalFiles':
            setShowImportLocalFilesModal?.(true);
            break;
        case 'importGoogleDocs':
            setShowImportGoogleDocsModal?.(true);
            break;
        default:
    }
    setShowMenuDropdown(null);
};
